import { useEffect } from 'react';
import { useBooking } from '@features/booking/state';
import { useAuth } from '@core/auth/AuthProvider';
import { useRestaurant } from '@core/tenant/TenantProvider';
import type { TermAndCondition } from '@features/booking/types/timeSlots';
import { useTimeSlots } from './useTimeSlots';

const NO_TERMS: TermAndCondition[] = [];

/**
 * Slot terms for the selected date, service and party size, in the guest's
 * current language.
 *
 * `booking.slotTerms` holds whatever language was active when the slots
 * loaded; the backend translates per request locale. Keeping the search
 * mounted here (its key includes the language) makes every language change
 * re-request the slots, and the fresh terms are stored back so they survive
 * navigation and the auth round-trip.
 */
export function useSlotTerms(): TermAndCondition[] {
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();

  const { service, selectedDate, diners, setSlotTerms } = booking;
  const storedTerms = booking.slotTerms ?? NO_TERMS;
  // Waiting-list bookings have no slot, so slot terms never apply to them.
  const termsApply = booking.flowType !== 'waiting_list' && storedTerms.length > 0;

  const { slotsByService } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: diners,
    date: selectedDate,
    serviceTypes: service ? [service] : [],
    enabled: termsApply && !!service && !!selectedDate && diners > 0,
    hasPreferredAccess: !!authState.preferred,
    meitreNetworkMenuId: booking.meitreNetworkMenuId,
    areaFilterIds: booking.areaFilterIds,
  });

  const fetchedTerms = service ? slotsByService[service]?.center?.termsAndConditions : undefined;

  // Only a non-empty result replaces what is stored. This hook is inert unless
  // terms already exist, so an empty array is never new information — it can
  // only drop a consent gate the guest was already shown.
  const hasFreshTerms = !!fetchedTerms?.length;

  useEffect(() => {
    if (hasFreshTerms && fetchedTerms !== storedTerms) setSlotTerms(fetchedTerms);
  }, [hasFreshTerms, fetchedTerms, storedTerms, setSlotTerms]);

  return hasFreshTerms ? fetchedTerms : storedTerms;
}
